// Apolo IDE — useCompareMode hook (Sprint v4 Epic S1 WS S1.3).
//
// Manages the local state for a Compare Mode session:
//   - active        : is Compare Mode toggled on?
//   - models        : the current model lineup
//   - busy/error    : dispatch state
//   - results       : array of { model, response, latencyMs, costUsd, status }
//   - dispatchId    : last dispatch's id (server-issued)
//
// Plus the actions: toggle, setModels, runCompare, pickWinner, reset.
//
// The hook does not own the network layer — it calls a `dispatch` function
// passed in via `deps.dispatch` so unit tests can stub the API. By default
// it uses `apiFetch('/api/agents/compare')`.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import {
  COMPARE_DEFAULT_MODELS,
  validateCompareSelection,
} from '../lib/compareDefaults.js';

async function defaultDispatch({ prompt, models, sessionId, systemPrompt }) {
  return apiFetch('/api/agents/compare', {
    method: 'POST',
    body: { prompt, models, sessionId, systemPrompt },
  });
}

export default function useCompareMode(options = {}) {
  const initialModels = options.initialModels || COMPARE_DEFAULT_MODELS;
  const dispatch = options.dispatch || defaultDispatch;
  const [active, setActive] = useState(false);
  const [models, setModels] = useState(initialModels);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [dispatchId, setDispatchId] = useState(null);
  const [winner, setWinner] = useState(null);
  const [totalCostUsd, setTotalCostUsd] = useState(0);

  // Shortcut listener — ⇧⌘P (or Shift+Ctrl+P on Windows/Linux).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function handler(e) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setActive((v) => !v);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggle = useCallback((next) => {
    setActive((v) => (typeof next === 'boolean' ? next : !v));
  }, []);

  const updateModels = useCallback((next) => {
    const v = validateCompareSelection(next);
    if (!v.ok) {
      setError(v.error);
      return false;
    }
    setError(null);
    setModels(next);
    return true;
  }, []);

  const reset = useCallback(() => {
    setResults([]);
    setDispatchId(null);
    setWinner(null);
    setTotalCostUsd(0);
    setError(null);
  }, []);

  const runCompare = useCallback(
    async ({ prompt, sessionId, systemPrompt }) => {
      const v = validateCompareSelection(models);
      if (!v.ok) {
        setError(v.error);
        return { ok: false, error: v.error };
      }
      if (typeof prompt !== 'string' || prompt.trim().length < 10) {
        const errMsg = 'Prompt must be at least 10 characters';
        setError(errMsg);
        return { ok: false, error: errMsg };
      }
      setBusy(true);
      setError(null);
      setResults([]);
      setWinner(null);
      try {
        const res = await dispatch({ prompt: prompt.trim(), models, sessionId, systemPrompt });
        if (!res.ok) {
          setError(res.error || 'Compare failed');
          return { ok: false, error: res.error };
        }
        const data = res.data || res;
        setResults(Array.isArray(data.results) ? data.results : []);
        setDispatchId(data.dispatchId || null);
        setTotalCostUsd(Number(data.totalCostUsd || 0));
        return { ok: true, data };
      } catch (err) {
        const m = err && err.message ? err.message : String(err);
        setError(m);
        return { ok: false, error: m };
      } finally {
        setBusy(false);
      }
    },
    [dispatch, models],
  );

  const pickWinner = useCallback(
    (subagentId) => {
      const found = results.find((r) => r.subagentId === subagentId);
      if (!found) return null;
      setWinner(found);
      return found;
    },
    [results],
  );

  return {
    // state
    active,
    models,
    busy,
    error,
    results,
    dispatchId,
    winner,
    totalCostUsd,
    // actions
    toggle,
    setModels: updateModels,
    runCompare,
    pickWinner,
    reset,
  };
}
